/**
 * Servizio di export PDF per utenti Pro.
 * Genera e scarica PDF (report fiscale, elenco fatture, lista F24) usando jsPDF puro
 * (nessuna dipendenza aggiuntiva: le tabelle sono disegnate manualmente con doc.text/doc.line).
 */

import { jsPDF } from "jspdf";

// Colore brand Forfy "Sunset Calm" per header (#FF8A5C in RGB).
const BRAND_COLOR = [255, 138, 92];
const MARGIN = 15;
const PAGE_BOTTOM_LIMIT = 275;

// Formattazione italiana: 1.200,50 €
function formatCurrency(value) {
  const num = Number(value || 0);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  const [intPart, decPart] = abs.toFixed(2).split(".");
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${groupedInt},${decPart} €`;
}

function formatDateIT(isoDate) {
  if (!isoDate) return "—";
  const parts = String(isoDate).split("-");
  if (parts.length !== 3) return isoDate;
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

function formatDateTimeIT(date) {
  return new Intl.DateTimeFormat("it-IT", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

/**
 * Disegna l'header brandizzato "Forfy" + titolo report + anno su ogni pagina.
 * Ritorna la coordinata Y da cui iniziare il contenuto.
 */
function drawHeader(doc, title, year) {
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BRAND_COLOR);
  doc.text("Forfy", MARGIN, 18);

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(120, 120, 120);
  doc.text(title, pageWidth - MARGIN, 14, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(30, 30, 30);
  doc.text(String(year), pageWidth - MARGIN, 21, { align: "right" });

  doc.setDrawColor(...BRAND_COLOR);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, 25, pageWidth - MARGIN, 25);

  doc.setTextColor(0, 0, 0);
  return 34;
}

/**
 * Disegna il footer ("Generato il ...") + numero pagina su tutte le pagine del documento.
 * Va chiamato UNA VOLTA a fine generazione, dopo aver creato tutte le pagine.
 */
function drawFooterOnAllPages(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const generatedAt = `Generato il ${formatDateTimeIT(new Date())}`;

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(150, 150, 150);
    doc.text(generatedAt, MARGIN, pageHeight - 10);
    doc.text(`Pagina ${i} di ${pageCount}`, pageWidth - MARGIN, pageHeight - 10, {
      align: "right",
    });
  }
  doc.setTextColor(0, 0, 0);
}

/** Verifica se serve una nuova pagina e, in caso, la crea e ridisegna l'header. */
function ensureSpace(doc, y, title, year, needed = 8) {
  if (y + needed > PAGE_BOTTOM_LIMIT) {
    doc.addPage();
    return drawHeader(doc, title, year);
  }
  return y;
}

function saveWithFooter(doc, filename) {
  drawFooterOnAllPages(doc);
  doc.save(filename);
}

/**
 * Esporta il riepilogo fiscale annuale (incassi, INPS, tasse stimate, netto).
 * @param {number} year - anno di riferimento.
 * @param {object} data - dati riepilogo, tipicamente derivati da fiscalCalc in DashboardPage:
 *   { incassiAnno, revenueYTD, inpsDovuto, inpsDaPagare, tasseLorde, tasseDaPagare,
 *     totaleDovuto, versatoTotale, residuoDaPagare, accantonamento, nettoDisponibile,
 *     aliquotaAutomatica, coefficienteRedditivita, proiezioneIncassi }
 */
export function exportFiscalReport(year, data = {}) {
  const doc = new jsPDF();
  let y = drawHeader(doc, "Report Fiscale", year);

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(`Riepilogo fiscale ${year}`, MARGIN, y);
  y += 10;

  const rows = [
    ["Fatturato emesso", formatCurrency(data.revenueYTD)],
    ["Incassi (principio di cassa)", formatCurrency(data.incassiAnno)],
    data.proiezioneIncassi != null
      ? ["Proiezione incassi fine anno", formatCurrency(data.proiezioneIncassi)]
      : null,
    data.coefficienteRedditivita != null
      ? ["Coefficiente redditività", `${Math.round(data.coefficienteRedditivita * 100)}%`]
      : null,
    data.aliquotaAutomatica != null
      ? ["Aliquota imposta sostitutiva", `${data.aliquotaAutomatica}%`]
      : null,
    null, // separatore
    ["INPS dovuto", formatCurrency(data.inpsDovuto)],
    ["INPS da versare", formatCurrency(data.inpsDaPagare)],
    ["Imposta sostitutiva lorda", formatCurrency(data.tasseLorde)],
    ["Imposta da versare", formatCurrency(data.tasseDaPagare)],
    null,
    ["Totale dovuto (INPS + imposta)", formatCurrency(data.totaleDovuto)],
    ["Già versato nell'anno", formatCurrency(data.versatoTotale)],
    ["Residuo da versare", formatCurrency(data.residuoDaPagare)],
    null,
    ["Accantonamento consigliato", formatCurrency(data.accantonamento)],
    ["Netto disponibile stimato", formatCurrency(data.nettoDisponibile)],
  ].filter(Boolean);

  doc.setFontSize(10);
  const labelX = MARGIN;
  const valueX = doc.internal.pageSize.getWidth() - MARGIN;

  rows.forEach(([label, value]) => {
    y = ensureSpace(doc, y, "Report Fiscale", year, 8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(label, labelX, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(20, 20, 20);
    doc.text(value, valueX, y, { align: "right" });
    y += 7;
  });

  y += 4;
  y = ensureSpace(doc, y, "Report Fiscale", year, 10);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, doc.internal.pageSize.getWidth() - MARGIN, y);
  y += 6;
  doc.setFontSize(8);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(140, 140, 140);
  doc.text(
    "Stima indicativa basata sui dati inseriti in Forfy. Verificare con il proprio commercialista.",
    MARGIN,
    y
  );

  saveWithFooter(doc, `forfy-report-fiscale-${year}.pdf`);
}

/**
 * Esporta l'elenco fatture dell'anno (tabella: data, numero, cliente, importo, stato).
 * @param {number} year - anno di riferimento.
 * @param {Array<object>} invoices - TUTTE le fatture (di tutti gli anni) { issueDate,
 *   invoiceCode, clientName, grossAmount, paid, paymentDate }. La lista/il fatturato mostrati
 *   sono filtrati per competenza (issueDate nell'anno), mentre l'incassato è calcolato per
 *   principio di cassa (paymentDate nell'anno, fallback issueDate se pagata senza paymentDate).
 */
export function exportInvoicesList(year, invoices = []) {
  const doc = new jsPDF();
  const title = "Elenco Fatture";
  let y = drawHeader(doc, title, year);
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(`Fatture ${year}`, MARGIN, y);
  y += 4;

  const inYear = (d) => d && new Date(d).getFullYear() === year;

  // Lista e fatturato: per competenza (fatture emesse nell'anno).
  const sorted = [...invoices]
    .filter((inv) => inYear(inv.issueDate))
    .sort((a, b) => new Date(a.issueDate || 0) - new Date(b.issueDate || 0));

  const totalEmesso = sorted.reduce((sum, inv) => sum + (Number(inv.grossAmount) || 0), 0);

  // Incassato: per cassa (fatture pagate con paymentDate, o issueDate come fallback, nell'anno).
  const totalIncassato = invoices
    .filter((inv) => inv.paid && inYear(inv.paymentDate || inv.issueDate))
    .reduce((sum, inv) => sum + (Number(inv.grossAmount) || 0), 0);

  y += 6;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80, 80, 80);
  doc.text(
    `Totale fatture: ${sorted.length}   ·   Fatturato: ${formatCurrency(
      totalEmesso
    )}   ·   Incassato: ${formatCurrency(totalIncassato)}`,
    MARGIN,
    y
  );
  y += 8;

  // Colonne tabella
  const cols = [
    { key: "data", label: "Data", x: MARGIN, width: 22 },
    { key: "numero", label: "Numero", x: MARGIN + 22, width: 32 },
    { key: "cliente", label: "Cliente", x: MARGIN + 54, width: 62 },
    { key: "importo", label: "Importo", x: MARGIN + 116, width: 32, align: "right" },
    { key: "stato", label: "Stato", x: pageWidth - MARGIN - 24, width: 24, align: "right" },
  ];

  function drawTableHeader() {
    doc.setFillColor(250, 240, 235);
    doc.rect(MARGIN, y - 5, pageWidth - MARGIN * 2, 7, "F");
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(60, 60, 60);
    cols.forEach((c) => {
      doc.text(c.label, c.align === "right" ? c.x + c.width : c.x, y, {
        align: c.align === "right" ? "right" : "left",
      });
    });
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
  }

  drawTableHeader();

  if (sorted.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text("Nessuna fattura per l'anno selezionato.", MARGIN, y);
    y += 6;
  }

  doc.setFontSize(8.5);
  sorted.forEach((inv, idx) => {
    if (y + 6 > PAGE_BOTTOM_LIMIT) {
      doc.addPage();
      y = drawHeader(doc, title, year);
      y += 4;
      drawTableHeader();
      doc.setFontSize(8.5);
    }

    if (idx % 2 === 1) {
      doc.setFillColor(250, 250, 250);
      doc.rect(MARGIN, y - 4.2, pageWidth - MARGIN * 2, 5.6, "F");
    }

    const stato = inv.paid ? "Incassata" : "Da incassare";
    doc.setTextColor(30, 30, 30);
    doc.setFont("helvetica", "normal");
    doc.text(formatDateIT(inv.issueDate), cols[0].x, y);
    doc.text(String(inv.invoiceCode || "-"), cols[1].x, y);
    const clientName = String(inv.clientName || "-");
    doc.text(clientName.length > 34 ? clientName.slice(0, 33) + "…" : clientName, cols[2].x, y);
    doc.setFont("helvetica", "bold");
    doc.text(formatCurrency(inv.grossAmount), cols[3].x + cols[3].width, y, { align: "right" });
    doc.setFont("helvetica", "normal");
    if (inv.paid) doc.setTextColor(21, 128, 61);
    else doc.setTextColor(180, 120, 20);
    doc.text(stato, cols[4].x + cols[4].width, y, { align: "right" });
    doc.setTextColor(0, 0, 0);

    y += 5.6;
  });

  saveWithFooter(doc, `forfy-elenco-fatture-${year}.pdf`);
}

/**
 * Esporta la lista F24 generati per l'anno.
 * @param {number} year - anno di riferimento.
 * @param {Array<object>} f24Items - F24 dell'anno { dataScadenza, descrizione, codiceTributo,
 *   importo, dataPagamento }.
 */
export function exportF24List(year, f24Items = []) {
  const doc = new jsPDF();
  const title = "Lista F24";
  let y = drawHeader(doc, title, year);
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(`F24 ${year}`, MARGIN, y);
  y += 4;

  const sorted = [...f24Items].sort((a, b) =>
    String(a.dataScadenza || "").localeCompare(String(b.dataScadenza || ""))
  );

  const totaleImporto = sorted.reduce((sum, f) => sum + (Number(f.importo) || 0), 0);
  const totalePagato = sorted
    .filter((f) => f.dataPagamento)
    .reduce((sum, f) => sum + (Number(f.importo) || 0), 0);

  y += 6;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80, 80, 80);
  doc.text(
    `Totale F24: ${sorted.length}   ·   Importo totale: ${formatCurrency(
      totaleImporto
    )}   ·   Pagato: ${formatCurrency(totalePagato)}`,
    MARGIN,
    y
  );
  y += 8;

  const cols = [
    { key: "scadenza", label: "Scadenza", x: MARGIN, width: 24 },
    { key: "descrizione", label: "Descrizione", x: MARGIN + 24, width: 68 },
    { key: "tributo", label: "Tributo", x: MARGIN + 92, width: 22 },
    { key: "importo", label: "Importo", x: MARGIN + 116, width: 30, align: "right" },
    { key: "stato", label: "Stato", x: pageWidth - MARGIN - 26, width: 26, align: "right" },
  ];

  function drawTableHeader() {
    doc.setFillColor(250, 240, 235);
    doc.rect(MARGIN, y - 5, pageWidth - MARGIN * 2, 7, "F");
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(60, 60, 60);
    cols.forEach((c) => {
      doc.text(c.label, c.align === "right" ? c.x + c.width : c.x, y, {
        align: c.align === "right" ? "right" : "left",
      });
    });
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
  }

  drawTableHeader();

  if (sorted.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text("Nessun F24 per l'anno selezionato.", MARGIN, y);
    y += 6;
  }

  const today = new Date().toISOString().split("T")[0];
  doc.setFontSize(8.5);
  sorted.forEach((f, idx) => {
    if (y + 6 > PAGE_BOTTOM_LIMIT) {
      doc.addPage();
      y = drawHeader(doc, title, year);
      y += 4;
      drawTableHeader();
      doc.setFontSize(8.5);
    }

    if (idx % 2 === 1) {
      doc.setFillColor(250, 250, 250);
      doc.rect(MARGIN, y - 4.2, pageWidth - MARGIN * 2, 5.6, "F");
    }

    const stato = f.dataPagamento ? "Pagato" : f.dataScadenza < today ? "Scaduto" : "Da pagare";
    doc.setTextColor(30, 30, 30);
    doc.setFont("helvetica", "normal");
    doc.text(formatDateIT(f.dataScadenza), cols[0].x, y);
    const descr = String(f.descrizione || f.tipo || "-");
    doc.text(descr.length > 42 ? descr.slice(0, 41) + "…" : descr, cols[1].x, y);
    doc.text(String(f.codiceTributo || "-"), cols[2].x, y);
    doc.setFont("helvetica", "bold");
    doc.text(formatCurrency(f.importo), cols[3].x + cols[3].width, y, { align: "right" });
    doc.setFont("helvetica", "normal");
    if (f.dataPagamento) doc.setTextColor(21, 128, 61);
    else if (f.dataScadenza < today) doc.setTextColor(190, 40, 40);
    else doc.setTextColor(180, 120, 20);
    doc.text(stato, cols[4].x + cols[4].width, y, { align: "right" });
    doc.setTextColor(0, 0, 0);

    y += 5.6;
  });

  saveWithFooter(doc, `forfy-lista-f24-${year}.pdf`);
}
